import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setShowFreeSpinIntro,
  setShowFreeSpinInfo,
  setShowFreeSpinOutro,
  setTriggerFreeSpinInfoOutPhase,
} from "../State/Slices/animationsSlice";
import {
  setFreeSpinMode,
  resetFreeSpinsState,
} from "../State/Slices/freeSpinsSlice";
import { resetAnimationsState } from "../State/Slices/animationsSlice";

const useAnimations = () => {
  const dispatch = useDispatch();
  const { gameType, currentRound, roundsLength, currentRoundIndex } =
    useSelector((state) => state.game);
  const { freeSpinsLeft } = useSelector((state) => state.freeSpins);
  const { linesRotationFinished, spinComplete } = useSelector(
    (state) => state.spin
  );
  const {
    freeSpinIntroAnimationCompleted,
    freeSpinInfoAnimationStarted,
    freeSpinOutroAnimationCompleted,
  } = useSelector((state) => state.animations);

  const triggerFreeSpinIntro = useCallback(() => {
    console.log("Triggering Free Spin Intro...");
    dispatch(setShowFreeSpinIntro(true));
    dispatch(setFreeSpinMode(true));
  }, [dispatch]);

  useEffect(() => {
    if (
      gameType === "feature" &&
      linesRotationFinished &&
      spinComplete &&
      !freeSpinIntroAnimationCompleted // ✅ Prevent multiple triggers
    ) {
      console.log("Conditions met for Free Spin Intro");
      triggerFreeSpinIntro();
    }
  }, [
    triggerFreeSpinIntro,
    linesRotationFinished,
    gameType,
    spinComplete,
    freeSpinIntroAnimationCompleted, // ✅ Prevents re-triggering
  ]);

  const triggerFreeSpinInfo = useCallback(() => {
    console.log("Triggering Free Spin Info...");
    dispatch(setShowFreeSpinInfo(true));
    dispatch(setFreeSpinMode(true));
  }, [dispatch]);

  useEffect(() => {
    if (
      currentRoundIndex > 0 &&
      gameType === "feature" &&
      linesRotationFinished &&
      spinComplete
    ) {
      console.log("Conditions met for Free Spin Info");
      triggerFreeSpinInfo();
    }
  }, [
    triggerFreeSpinInfo,
    linesRotationFinished,
    spinComplete,
    currentRoundIndex,
    gameType,
  ]);

  useEffect(() => {
    let timeoutId;

    if (freeSpinInfoAnimationStarted && spinComplete && linesRotationFinished) {
      timeoutId = setTimeout(() => {
        dispatch(setTriggerFreeSpinInfoOutPhase(true));
      }, 1500);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    freeSpinInfoAnimationStarted,
    dispatch,
    spinComplete,
    linesRotationFinished,
  ]);

  const triggerFreeSpinOutro = useCallback(() => {
    console.log("Triggering Free Spin Outro...");
    dispatch(setShowFreeSpinOutro(true));
  }, [dispatch]);

  useEffect(() => {
    let timeoutId;

    if (freeSpinIntroAnimationCompleted && freeSpinsLeft === 0) {
      console.log(
        "Free spin intro completed, checking for outro conditions..."
      );

      if (
        (spinComplete && currentRound?.win === 0) || // ✅ More explicit check
        (currentRound?.win > 0 && linesRotationFinished) // ✅ Valid win condition
      ) {
        console.log("Triggering Free Spin Outro with delay...");
        timeoutId = setTimeout(() => {
          if (freeSpinsLeft === 0) {
            // ✅ Ensure outro only plays if free spins are exhausted
            triggerFreeSpinOutro();
          } else {
            console.log("⛔ Free spins still ongoing, blocking outro.");
          }
        }, 500);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    dispatch,
    linesRotationFinished,
    spinComplete,
    roundsLength,
    freeSpinIntroAnimationCompleted,
    freeSpinsLeft,
    triggerFreeSpinOutro,
    currentRound,
  ]);

  useEffect(() => {
    if (freeSpinOutroAnimationCompleted) {
      dispatch(resetAnimationsState());
      dispatch(resetFreeSpinsState());
    }
  }, [freeSpinOutroAnimationCompleted, dispatch]);

  return {};
};

export default useAnimations;
